import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Loader2, Check, X, KeyRound, PartyPopper } from "lucide-react";
import { checkPasswordStrength, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "@nexus/contracts";
import { verifyResetCode, confirmReset } from "@/features/auth/lib/resetPassword";
import { PasswordStrengthMeter } from "@/features/auth/components/PasswordStrengthMeter";
import { Sentry } from "@/lib/sentry";
import {
  AdgeLogo,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  PasswordInput,
} from "@nexus/ui";

interface ResetPasswordFormProps {
  oobCode: string;
}

type Step = "verifying" | "form" | "done" | "error";

interface Rule {
  key: string;
  label: string;
  met: boolean;
}

function usePasswordRules(password: string, confirm: string): Rule[] {
  return [
    { key: "length", label: "auth.ruleMinLength", met: password.length >= PASSWORD_MIN_LENGTH },
    {
      key: "strength",
      label: "auth.ruleNotCommon",
      met: password.length >= PASSWORD_MIN_LENGTH && checkPasswordStrength(password) === null,
    },
    { key: "match", label: "auth.rulePasswordsMatch", met: confirm.length > 0 && password === confirm },
  ];
}

function RuleRow({ met, label }: { met: boolean; label: string }) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-1.5 text-xs">
      {met
        ? <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
        : <X className="h-3.5 w-3.5 shrink-0 text-[var(--muted-foreground)]" aria-hidden="true" />}
      <span className={met ? "text-success" : "text-[var(--muted-foreground)]"}>
        {t(label)}
      </span>
    </li>
  );
}

export function ResetPasswordForm({ oobCode }: ResetPasswordFormProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>("verifying");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  const rules = usePasswordRules(password, confirm);
  const allRulesMet = rules.every((r) => r.met);

  useEffect(() => {
    verifyResetCode(oobCode)
      .then((resolvedEmail) => {
        setEmail(resolvedEmail);
        setStep("form");
      })
      .catch(() => setStep("error"));
  }, [oobCode]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched(true);
    setSubmitError(null);
    if (!allRulesMet) return;

    setLoading(true);
    try {
      await confirmReset(oobCode, password);
      setStep("done");
    } catch (err) {
      Sentry.captureException(err, { tags: { flow: "reset-password" } });
      setSubmitError(t("auth.resetPasswordError"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-[var(--background)] p-4">
      <Card className="w-full max-w-sm">

        {step === "verifying" && (
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--muted-foreground)]" aria-label={t("common.loading")} />
          </CardContent>
        )}

        {step === "error" && (
          <>
            <CardHeader className="space-y-1 text-start">
              <AdgeLogo className="mb-2 origin-start" aria-label={t("auth.brandName")} />
              <CardTitle className="text-xl font-normal">{t("auth.resetPasswordExpiredTitle")}</CardTitle>
              <CardDescription>{t("auth.resetPasswordExpiredDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/auth/forgot-password">
                <Button className="w-full">{t("auth.resetPasswordRequestNew")}</Button>
              </Link>
            </CardContent>
          </>
        )}

        {step === "form" && (
          <>
            <CardHeader className="space-y-1 text-start">
              <AdgeLogo className="mb-2 origin-start" aria-label={t("auth.brandName")} />
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-[var(--muted-foreground)]" aria-hidden="true" />
                <CardTitle className="text-xl font-normal">{t("auth.resetPasswordTitle")}</CardTitle>
              </div>
              {email && (
                <CardDescription className="text-sm">
                  {t("auth.resetPasswordFor", { email })}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="new-password">{t("auth.newPassword")}</Label>
                  <PasswordInput
                    id="new-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setSubmitError(null); }}
                    required
                    autoFocus
                    minLength={PASSWORD_MIN_LENGTH}
                    maxLength={PASSWORD_MAX_LENGTH}
                    disabled={loading}
                    autoComplete="new-password"
                    aria-describedby="password-rules"
                    showPasswordLabel={t("auth.showPassword")}
                    hidePasswordLabel={t("auth.hidePassword")}
                  />
                  <PasswordStrengthMeter password={password} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">{t("auth.confirmPassword")}</Label>
                  <PasswordInput
                    id="confirm-password"
                    placeholder="••••••••"
                    value={confirm}
                    onChange={(e) => { setConfirm(e.target.value); setSubmitError(null); }}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    maxLength={PASSWORD_MAX_LENGTH}
                    disabled={loading}
                    autoComplete="new-password"
                    showPasswordLabel={t("auth.showPassword")}
                    hidePasswordLabel={t("auth.hidePassword")}
                  />
                </div>

                {/* Live requirement checklist — shown once user starts typing */}
                {(password.length > 0 || touched) && (
                  <ul id="password-rules" className="space-y-1" aria-label={t("auth.passwordRequirements")}>
                    {rules.map((r) => (
                      <RuleRow key={r.key} met={r.met} label={r.label} />
                    ))}
                  </ul>
                )}

                {submitError && (
                  <p role="alert" className="text-sm text-[var(--destructive)]">{submitError}</p>
                )}

                <Button
                  type="submit"
                  className="w-full gap-2"
                  disabled={loading || (touched && !allRulesMet)}
                  aria-busy={loading}
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  <span>{loading ? t("auth.resettingPassword") : t("auth.resetPasswordSubmit")}</span>
                </Button>
              </form>
            </CardContent>
          </>
        )}

        {step === "done" && (
          <>
            <CardHeader className="space-y-1 text-start">
              <AdgeLogo className="mb-2 origin-start" aria-label={t("auth.brandName")} />
              <div className="flex items-center gap-2">
                <PartyPopper className="h-5 w-5 text-[var(--muted-foreground)]" aria-hidden="true" />
                <CardTitle className="text-xl font-normal">{t("auth.resetPasswordDoneTitle")}</CardTitle>
              </div>
              <CardDescription>{t("auth.resetPasswordDoneDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/login">
                <Button className="w-full">{t("auth.signIn")}</Button>
              </Link>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
